import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import { DecimalInput } from "@/components/form-control/decimal-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  NEW_FEE_FORM_PREFILL,
  FeeConfigFormValues,
  complementShare,
  createEmptyRule,
  feeConfigFormSchema,
  feeConfigFormToInput,
  feeConfigToFormValues,
  formatFrNumber,
  getFeeConfigVersionLabel,
  hasMaxTwoDecimals,
} from "../../fee-config.const";
import { FeeConfigEditorState } from "../../hooks/use-fee-configs";
import { SectionDivider } from "../section-divider";
import { FeeConfigPreview } from "./fee-config-preview";
import { FeeRuleFields } from "./fee-rule-fields";

type FeeConfigEditorModalProps = {
  state: FeeConfigEditorState;
  onClose: () => void;
  onSubmit: (values: FeeConfigFormValues) => Promise<void>;
};

export const FeeConfigEditorModal = ({ state, onClose, onSubmit }: FeeConfigEditorModalProps) => {
  const isReadOnly = state.mode === "view";
  const config = state.mode === "create" ? null : state.config;

  const form = useForm<FeeConfigFormValues>({
    resolver: zodResolver(feeConfigFormSchema),
    defaultValues: config ? feeConfigToFormValues(config) : NEW_FEE_FORM_PREFILL,
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting },
  } = form;

  const { fields: ruleFields, append: appendRule, remove: removeRule } = useFieldArray({ control, name: "rules" });

  const customerShare = useWatch({ control, name: "customerShare" });
  const watchedValues = useWatch({ control });
  const parsedForm = feeConfigFormSchema.safeParse(watchedValues);
  const previewConfig = parsedForm.success ? feeConfigFormToInput(parsedForm.data) : null;

  const title =
    state.mode === "create"
      ? "Nouvelle commission"
      : state.mode === "edit"
        ? "Modifier le brouillon"
        : `${config?.name ?? ""} (${config ? getFeeConfigVersionLabel(config) : ""})`;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] flex-col p-0 sm:max-w-3xl">
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pt-6 pb-4 border-b border-border">
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>
                  La commission s'ajoute au prix du stationnement. Les règles sont évaluées indépendamment et leurs
                  montants s'additionnent.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-6 space-y-6">
              <fieldset disabled={isReadOnly} className="space-y-6">
                <SectionDivider label="Informations générales" />

                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Nom <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Ex. Commission visiteur" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SectionDivider label="Répartition de la commission" />

                <FormField
                  control={control}
                  name="customerShare"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Part client (%) <span className="text-red-500">*</span>
                      </FormLabel>
                      <div className="flex items-center gap-3">
                        <FormControl>
                          <DecimalInput
                            className="w-28"
                            value={typeof field.value === "number" ? field.value : undefined}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        {typeof customerShare === "number" &&
                          customerShare >= 0 &&
                          customerShare <= 100 &&
                          hasMaxTwoDecimals(customerShare) && (
                            <span className="text-sm text-muted-foreground">
                              Part ville : {formatFrNumber(complementShare(customerShare))} %
                            </span>
                          )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        La part client est payée à l'achat, la part ville est réglée avec la ville.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SectionDivider label="Règles" />

                <div className="space-y-4">
                  {ruleFields.map((ruleField, ruleIndex) => (
                    <FeeRuleFields
                      key={ruleField.id}
                      control={control}
                      setValue={setValue}
                      ruleIndex={ruleIndex}
                      onRemove={ruleFields.length > 1 ? () => removeRule(ruleIndex) : undefined}
                    />
                  ))}
                </div>

                <Button type="button" variant="outline" size="sm" onClick={() => appendRule(createEmptyRule())}>
                  + Ajouter une règle (avancé)
                </Button>
              </fieldset>

              <SectionDivider label="Aperçu" />
              <FeeConfigPreview config={previewConfig} />
            </div>

            <div className="px-6 py-4 border-t border-border">
              <DialogFooter className={isReadOnly ? "sm:justify-end" : "sm:justify-between"}>
                {isReadOnly ? (
                  <Button variant="secondary" onClick={onClose} type="button">
                    Fermer
                  </Button>
                ) : (
                  <>
                    <Button variant="secondary" onClick={onClose} type="button" disabled={isSubmitting}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {state.mode === "create" ? "Créer le brouillon" : "Enregistrer le brouillon"}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
